/**
 * Community native video service.
 *
 * Talks to the native pipeline at /api/communities/:communityId/videos/* -- the
 * same GCS-upload -> branded-intro -> Cloudflare Stream machinery rituals and
 * profiles use. Two-step upload (mint signed URL, resumable PUT to GCS, then
 * confirm) and a signed playback token (community playback is always signed).
 */

import apiClient from "@/lib/api-client"

export type CommunityVideoStatus = "pending" | "processing" | "ready" | "failed"

/** One native community video row as projected by the backend list endpoint. */
export interface CommunityVideoItem {
  id: string
  communityId: string
  status: CommunityVideoStatus
  cloudflareUid: string | null
  duration: number | null
  thumbnailUrl: string | null
  title: string | null
  sortOrder: number
  /** Only populated for managers (creator / admins). */
  failureReason: string | null
  createdAt: string
  updatedAt: string
}

export interface RequestUploadUrlInput {
  filename: string
  contentType: string
  sizeBytes: number
  durationSeconds: number
}

export interface RequestUploadUrlResponse {
  signedUrl: string
  gcsPath: string
  contentType: string
  maxBytes: number
  expiresAt: number
}

export interface ConfirmUploadInput {
  gcsPath: string
  sortOrder?: number
  title?: string | null
}

export interface ConfirmUploadResponse {
  communityVideoId: string
  status: CommunityVideoStatus
  sortOrder: number
  gcsPath: string
}

export interface PlaybackTokenResponse {
  /** Signed JWT (community playback is always signed). */
  token: string | null
  expiresAt: number | null
  cloudflareUid: string
}

export const requestCommunityVideoUploadUrl = async (
  communityId: string,
  input: RequestUploadUrlInput,
): Promise<RequestUploadUrlResponse> => {
  const { data } = await apiClient.post<{ success: boolean; data: RequestUploadUrlResponse }>(
    `api/communities/${communityId}/videos/upload-url`,
    input,
  )
  return data.data
}

export const confirmCommunityVideoUpload = async (
  communityId: string,
  input: ConfirmUploadInput,
): Promise<ConfirmUploadResponse> => {
  const { data } = await apiClient.post<{ success: boolean; data: ConfirmUploadResponse }>(
    `api/communities/${communityId}/videos`,
    input,
  )
  return data.data
}

export const listCommunityVideos = async (communityId: string): Promise<CommunityVideoItem[]> => {
  const { data } = await apiClient.get<{ success: boolean; data: CommunityVideoItem[] }>(
    `api/communities/${communityId}/videos`,
  )
  return data.data
}

export const getCommunityVideoPlaybackToken = async (
  communityId: string,
  videoId: string,
): Promise<PlaybackTokenResponse> => {
  const { data } = await apiClient.get<{ success: boolean; data: PlaybackTokenResponse }>(
    `api/communities/${communityId}/videos/${videoId}/playback-token`,
  )
  return data.data
}

export const deleteCommunityVideo = async (communityId: string, videoId: string): Promise<void> => {
  await apiClient.delete(`api/communities/${communityId}/videos/${videoId}`)
}
